import { supabase } from "@/integrations/supabase/client";

export interface TenantBranding {
  logoUrl: string | null;
  /** Variante clara da logo (para fundos coloridos). Opcional. */
  logoUrlWhite: string | null;
  primaryColor: string; // hex
  companyName: string;
  companyPhone: string | null;
  companyEmail: string | null;
  companyAddress: string | null;
  companyDocument: string | null;
}

const DEFAULT_PRIMARY = "#16a34a";

/**
 * Convert HSL string like "222 47% 31%" to hex color.
 * Accepts formats: "H S% L%", "H, S%, L%", "hsl(H, S%, L%)"
 */
export function hslToHex(hsl: string): string {
  if (!hsl || hsl.trim() === "") return DEFAULT_PRIMARY;

  // Strip hsl() wrapper if present
  const clean = hsl.replace(/hsl\(|\)/gi, "").replace(/,/g, " ").trim();
  const parts = clean.split(/\s+/).map((p) => parseFloat(p.replace("%", "")));

  if (parts.length < 3 || parts.some(isNaN)) return DEFAULT_PRIMARY;

  let [h, s, l] = parts;
  s /= 100;
  l /= 100;

  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };

  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Fetch tenant branding from tenants table + tenant_settings.
 * Falls back to safe defaults for tenants without branding configured.
 */
export async function fetchTenantBranding(tenantId: string): Promise<TenantBranding> {
  const [tenantResult, settingsResult] = await Promise.all([
    supabase
      .from("tenants")
      .select("name, logo_url, phone, email, address")
      .eq("id", tenantId)
      .single(),
    supabase
      .from("tenant_settings" as any)
      .select("key, value")
      .eq("tenant_id", tenantId)
      .in("key", ["brand_primary_color", "brand_logo_url", "brand_logo_url_white"]),
  ]);

  const tenant = tenantResult.data as any;
  const settings = ((settingsResult.data || []) as unknown as Array<{ key: string; value: string }>);

  const settingsMap: Record<string, string> = {};
  settings.forEach((s) => {
    settingsMap[s.key] = s.value;
  });

  const hslColor = settingsMap["brand_primary_color"] || "";
  const brandLogo = settingsMap["brand_logo_url"] || "";
  const brandLogoWhite = settingsMap["brand_logo_url_white"] || "";

  return {
    logoUrl: brandLogo || tenant?.logo_url || null,
    logoUrlWhite: brandLogoWhite || null,
    primaryColor: hslColor ? hslToHex(hslColor) : DEFAULT_PRIMARY,
    companyName: tenant?.name || "",
    companyPhone: tenant?.phone || null,
    companyEmail: tenant?.email || null,
    companyAddress: tenant?.address || null,
    companyDocument: null,
  };
}
